import { memo, useMemo, useState, type ReactNode } from "react";
import {
  makeEconomy,
  buildFuelTrends,
  fuelLogsForCar,
  stationConsumptionTotals,
  gradeConsumptions,
  avgSpeedSegments,
  type CanonicalStore,
  type Car,
  type FuelLog,
  type TrendSeries,
  type TrendColor,
  type SpeedSegment,
} from "../../core/index.ts";
import { SplitFlapNumber } from "../components/SplitFlapNumber.tsx";
import { SplineChart } from "../components/SplineChart.tsx";
import { IconTrends, IconGrade, IconSpeed } from "../components/Icons.tsx";
import { BackRow } from "../forms/Modal.tsx";
import { fmt, longDate } from "../format.ts";
import { LogRow } from "./FuelView.tsx";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function monthKey(d: number | null | undefined): string {
  if (d == null) return "unknown";
  const dt = new Date(d * 1000);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

function yearKey(d: number | null | undefined): string {
  if (d == null) return "unknown";
  return String(new Date(d * 1000).getUTCFullYear());
}

export function extractPeriods(dates: (number | null | undefined)[]) {
  const months = new Set<string>();
  const years = new Set<string>();
  for (const d of dates) {
    months.add(monthKey(d));
    years.add(yearKey(d));
  }
  return {
    months: [...months].sort((a, b) => b.localeCompare(a)),
    years: [...years].sort((a, b) => b.localeCompare(a)),
  };
}

export function filterByPeriod<T>(items: T[], dateFn: (item: T) => number | null | undefined, period: string): T[] {
  if (period === "all") return items;
  if (period.startsWith("y-")) {
    const y = period.slice(2);
    return items.filter((item) => yearKey(dateFn(item)) === y);
  }
  return items.filter((item) => monthKey(dateFn(item)) === period);
}

const SELECT_STYLE: React.CSSProperties = {
  width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
  color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px",
  boxSizing: "border-box",
};

export function PeriodFilter({ period, setPeriod, months, years, total }: {
  period: string; setPeriod: (v: string) => void; months: string[]; years: string[]; total: number;
}) {
  return (
    <select style={{ ...SELECT_STYLE, marginTop: 12 }} value={period} onChange={(e) => setPeriod(e.target.value)}>
      <option value="all">ALL ({total})</option>
      {years.length > 1 && <optgroup label="YEARS">{years.map((y) => (
        <option key={y} value={`y-${y}`}>YEAR {y}</option>
      ))}</optgroup>}
      {months.length > 1 && <optgroup label="MONTHS">{months.map((m) => (
        <option key={m} value={m}>{monthLabel(m)}</option>
      ))}</optgroup>}
    </select>
  );
}

const COLOR: Record<TrendColor, string> = {
  amber: "var(--amber)",
  amberHot: "var(--amberHot)",
  cyan: "var(--cyan)",
  redline: "var(--redline)",
  lime: "var(--lime)",
};

type HubTab = "trends" | "consumption" | "grades" | "speed" | "entries";

export function InsightsView({ store, car, isPro, requirePro, onBack }: {
  store: CanonicalStore; car: Car; isPro?: boolean; requirePro?: () => void; onBack: () => void;
}) {
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const currency = store.profile.displayCurrency;
  const logs = fuelLogsForCar(store.fuelLogs, car.id);
  const series = buildFuelTrends(logs, economy, currency, car);
  const speed = avgSpeedSegments(logs, economy);
  const [hub, setHub] = useState<HubTab | null>(null);
  const [filterEntry, setFilterEntry] = useState<{ type: string; value: string } | null>(null);

  if (hub === "entries" && filterEntry) return <FilteredEntries logs={logs} economy={economy} currency={currency} filter={filterEntry} onBack={() => setHub(null)} />;
  if (hub === "trends") return <TrendsListView series={series} isPro={isPro} requirePro={requirePro} onBack={() => setHub(null)} />;
  if (hub === "consumption") return <ConsumptionStationView logs={logs} currency={currency} isPro={isPro} requirePro={requirePro} onBack={() => setHub(null)} onSelectEntry={(v) => { setFilterEntry({ type: "station", value: v }); setHub("entries"); }} />;
  if (hub === "grades") return <GradeView logs={logs} car={car} economy={economy} isPro={isPro} requirePro={requirePro} onBack={() => setHub(null)} onSelectEntry={(v) => { setFilterEntry({ type: "grade", value: v }); setHub("entries"); }} />;
  if (hub === "speed") return <SpeedView segments={speed} economy={economy} isPro={isPro} requirePro={requirePro} onBack={() => setHub(null)} />;

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">INSIGHTS</div>
        <div className="eyebrow amber" style={{ marginBottom: 14 }}>{car.model}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <InsightRow icon={<IconTrends />} title="TRENDS" subtitle={`${series.length} metrics over your recent entries`} onClick={() => setHub("trends")} />
          <InsightRow icon={<IconGrade />} title="CONSUMPTION BY STATION" subtitle={`${stationConsumptionTotals(logs).length} stations · volume & cost breakdown`} onClick={() => setHub("consumption")} />
          {gradeConsumptions(logs, economy, car).length > 0 && <InsightRow icon={<IconGrade />} title="CONSUMPTION BY GRADE" subtitle={`${gradeConsumptions(logs, economy, car).length} grades broken down`} onClick={() => setHub("grades")} />}
          {speed.length > 0 && <InsightRow icon={<IconSpeed />} title="AVG SPEED" subtitle="Speed trend across your entries" onClick={() => setHub("speed")} />}
        </div>
      </div>
    </div>
  );
}

function InsightRow({ icon, title, subtitle, onClick }: { icon: ReactNode; title: string; subtitle: string; onClick: () => void }) {
  return (
    <button className="panel" style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, cursor: "pointer", textAlign: "left", width: "100%" }} onClick={onClick}>
      <span style={{ display: "flex", alignItems: "center", color: "var(--amber)" }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div className="mono" style={{ fontSize: 12, fontWeight: 500, letterSpacing: 2, color: "var(--cream)" }}>{title}</div>
        <div className="mono muted" style={{ fontSize: 9, marginTop: 3 }}>{subtitle}</div>
      </div>
      <span className="mono muted" style={{ fontSize: 14 }}>›</span>
    </button>
  );
}

function TrendsListView({ series, isPro, requirePro, onBack }: {
  series: TrendSeries[]; isPro?: boolean; requirePro?: () => void; onBack: () => void;
}) {
  const [sel, setSel] = useState<TrendSeries | null>(null);

  if (sel) {
    if (isPro) return <TrendTable series={sel} onBack={() => setSel(null)} />;
    return <ProGate title={sel.title} onBack={() => setSel(null)} requirePro={requirePro} />;
  }

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">ALL TRENDS</div>

        {series.length === 0 && (
          <div className="panel mono muted" style={{ fontSize: 12 }}>Not enough data yet — add a few entries to see trends.</div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {series.map((s) => (
            <TrendCard key={s.title} series={s} isPro={isPro} onClick={() => setSel(s)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TrendCard({ series, isPro, onClick }: { series: TrendSeries; isPro?: boolean; onClick: () => void }) {
  const values = useMemo(() => series.points.map((p) => p.value), [series.points]);
  const c = COLOR[series.color];
  const latest = values.length ? values[values.length - 1]! : null;
  return (
    <button className="panel" style={{ textAlign: "left", cursor: "pointer", padding: 14, display: "flex", flexDirection: "column", gap: 10 }} onClick={onClick}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="mono muted" style={{ fontSize: 9, letterSpacing: 2.2 }}>{series.title}{!isPro ? " (Pro)" : ""}</span>
        <span style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.6, color: c }}>
          {latest != null ? `${fmt(latest)} ${series.unit.toUpperCase()}` : "—"}
        </span>
        <span className="mono muted" style={{ fontSize: 12 }}>›</span>
      </div>
      {values.length >= 2 ? (
        <>
          <SplineChart values={values} color={c} height={64} />
          <div style={{ display: "flex", fontSize: 9, letterSpacing: 1.6 }} className="mono">
            <span style={{ color: series.betterIsLower ? "var(--lime)" : "var(--muted)" }}>{series.minLabel} {fmt(Math.min(...values))}</span>
            <span style={{ flex: 1 }} />
            <span style={{ color: series.betterIsLower ? "var(--redline)" : "var(--lime)" }}>{series.maxLabel} {fmt(Math.max(...values))}</span>
          </div>
        </>
      ) : (
        <div className="mono muted" style={{ fontSize: 10 }}>Need at least 2 entries to trend this.</div>
      )}
      {series.extraInfo && <div className="mono lime" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.6 }}>{series.extraInfo}</div>}
    </button>
  );
}

export function TrendTable({ series, onBack }: { series: TrendSeries; onBack: () => void }) {
  const [period, setPeriod] = useState("all");
  const c = COLOR[series.color];

  const { months, years } = useMemo(() => extractPeriods(series.points.map((p) => p.date)), [series.points]);

  const filteredPoints = useMemo(() => filterByPeriod(series.points, (p) => p.date, period), [series.points, period]);

  const values = filteredPoints.map((p) => p.value);
  const has = values.length > 0;
  const min = has ? Math.min(...values) : 0;
  const max = has ? Math.max(...values) : 0;
  const avg = has ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const newest = [...filteredPoints].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">TREND · TABLE</div>
        <div className="mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.4, color: c }}>{series.title}</div>
        <div className="mono muted" style={{ fontSize: 10, marginTop: 4 }}>Unit · {series.unit}</div>

        {series.points.length > 0 && (
          <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={series.points.length} />
        )}

        {values.length >= 2
          ? <div className="panel" style={{ marginTop: 16 }}><SplineChart values={values} color={c} height={120} /></div>
          : <div className="panel mono muted" style={{ marginTop: 16, fontSize: 12 }}>Need at least 2 entries to chart this trend.</div>}

        {series.totalValue != null && (
          <div className="panel" style={{ marginTop: 16, display: "flex", alignItems: "center", borderColor: c, background: "color-mix(in srgb, " + c + " 10%, transparent)" }}>
            <span className="mono muted" style={{ fontSize: 10, letterSpacing: 2.4 }}>{series.totalLabel ?? "TOTAL"}</span>
            <span style={{ flex: 1 }} />
            <SplitFlapNumber value={fmt(series.totalValue)} size={22} weight={500} />
            <span className="mono" style={{ fontSize: 10, letterSpacing: 1.4, color: c, marginLeft: 6 }}>{series.unit.toUpperCase()}</span>
          </div>
        )}

        <div className="statstrip" style={{ marginTop: 16 }}>
          <Tile k={series.minLabel} v={has ? fmt(min) : "—"} u={series.unit} />
          <Tile k={series.maxLabel} v={has ? fmt(max) : "—"} u={series.unit} />
          <Tile k="AVG" v={has ? fmt(avg) : "—"} u={series.unit} />
        </div>

        {series.extraInfo && (
          <div className="mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.6, color: c, marginTop: 12, padding: 10, borderRadius: 10, border: `1px solid ${c}`, background: "color-mix(in srgb, " + c + " 8%, transparent)" }}>
            {series.extraInfo}
          </div>
        )}

        <div className="divider" style={{ marginTop: 18 }}><span className="label">READINGS · NEWEST FIRST</span><span className="line" /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {newest.map((p, i) => (
            <div key={i} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
              <span className="mono muted" style={{ fontSize: 11 }}>{longDate(p.date ?? null)}</span>
              <span className="mono" style={{ fontSize: 13 }}>{fmt(p.value)} <span className="muted" style={{ fontSize: 9 }}>{series.unit}</span></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConsumptionStationView({ logs, currency, isPro, requirePro, onBack, onSelectEntry }: {
  logs: FuelLog[]; currency: string; isPro?: boolean; requirePro?: () => void; onBack: () => void; onSelectEntry?: (name: string) => void;
}) {
  if (!isPro) return <ProGate title="CONSUMPTION BY STATION" onBack={onBack} requirePro={requirePro} />;

  const [period, setPeriod] = useState("all");
  const { months, years } = useMemo(() => extractPeriods(logs.map((l) => l.date)), [logs]);
  const filtered = useMemo(() => filterByPeriod(logs, (l) => l.date, period), [logs, period]);
  const totals = useMemo(() => stationConsumptionTotals(filtered), [filtered]);

  const totalCost = totals.reduce((s, t) => s + t.totalCost, 0);
  const totalVol = totals.reduce((s, t) => s + t.totalVolume, 0);
  const totalEntries = totals.reduce((s, t) => s + t.entryCount, 0);
  const unit = totals.some((t) => t.hasKwh) ? "L+kWh" : "L";
  const sorted = [...totals].sort((a, b) => b.totalCost - a.totalCost);

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">CONSUMPTION BY STATION</div>

        <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={logs.length} />

        <div className="statstrip" style={{ marginTop: 12 }}>
          <Tile k="STATIONS" v={String(totals.length)} />
          <Tile k="ENTRIES" v={String(totalEntries)} />
          <Tile k="TOTAL VOL" v={fmt(Math.round(totalVol * 10) / 10)} u={unit} />
          <Tile k="TOTAL" v={String(Math.round(totalCost))} u={currency} />
        </div>

        <div className="divider" style={{ marginTop: 18 }}><span className="label">STATION · VOL · COST · LOGS · AVG/L</span><span className="line" /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {sorted.map((t) => (
            <div key={t.name} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", cursor: "pointer" }} onClick={() => onSelectEntry?.(t.name)}>
              <span className="mono" style={{ fontSize: 11, color: "var(--cream)", flex: 1 }}>{t.name}</span>
              <span className="mono" style={{ fontSize: 11, color: "var(--cyan)", textAlign: "right", minWidth: 55 }}>{fmt(t.totalVolume)} <span className="muted" style={{ fontSize: 8 }}>{t.hasKwh ? "L" : unit}</span></span>
              <span className="mono" style={{ fontSize: 11, textAlign: "right", minWidth: 60 }}>{Math.round(t.totalCost)} <span className="muted" style={{ fontSize: 8 }}>{currency}</span></span>
              <span className="mono muted" style={{ fontSize: 10, textAlign: "right", minWidth: 24 }}>{t.entryCount}</span>
              <span className="mono muted" style={{ fontSize: 10, textAlign: "right", minWidth: 50 }}>{t.avgPricePerUnit.toFixed(2)} <span className="muted" style={{ fontSize: 8 }}>{currency}/{t.hasKwh ? "kWh" : "L"}</span></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GradeView({ logs, car, economy, isPro, requirePro, onBack, onSelectEntry }: {
  logs: FuelLog[]; car: Car; economy: ReturnType<typeof makeEconomy>; isPro?: boolean; requirePro?: () => void; onBack: () => void; onSelectEntry?: (grade: string) => void;
}) {
  if (!isPro) return <ProGate title="GRADES" onBack={onBack} requirePro={requirePro} />;

  const [period, setPeriod] = useState("all");
  const { months, years } = useMemo(() => extractPeriods(logs.map((l) => l.date)), [logs]);
  const filtered = useMemo(() => filterByPeriod(logs, (l) => l.date, period), [logs, period]);
  const grades = useMemo(() => gradeConsumptions(filtered, economy, car), [filtered, economy, car]);

  const overallAvg = grades.length > 0
    ? grades.reduce((s, g) => s + g.avgConsumption, 0) / grades.length
    : 0;
  const isEvLabel = economy.energyEfficiencyLabel();
  const isFuelLabel = economy.efficiencyLabel();
  const label = grades.some((g) => g.grade === "Electric") ? isEvLabel : isFuelLabel;
  const sorted = [...grades].sort((a, b) => a.avgConsumption - b.avgConsumption);

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">GRADES</div>

        <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={logs.length} />

        <div className="statstrip" style={{ marginTop: 12 }}>
          <Tile k="GRADES" v={String(grades.length)} />
          <Tile k="ENTRIES" v={String(grades.reduce((s, g) => s + g.fillCount, 0))} />
          <Tile k="AVG" v={fmt(Math.round(overallAvg * 100) / 100)} u={label} />
        </div>

        <div className="divider" style={{ marginTop: 18 }}><span className="label">GRADE · AVG · LOGS · DIST</span><span className="line" /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {sorted.map((g) => (
            <div key={g.grade} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", cursor: "pointer" }} onClick={() => onSelectEntry?.(g.grade)}>
              <span className="mono" style={{ fontSize: 11, color: "var(--cream)", flex: 1 }}>{g.grade}</span>
              <span className="mono" style={{ fontSize: 11, color: g.avgConsumption <= overallAvg ? "var(--lime)" : "var(--amber)", textAlign: "right", minWidth: 70 }}>{fmt(g.avgConsumption)} <span className="muted" style={{ fontSize: 8 }}>{label}</span></span>
              <span className="mono muted" style={{ fontSize: 10, textAlign: "right", minWidth: 30 }}>{g.fillCount}</span>
              <span className="mono muted" style={{ fontSize: 10, textAlign: "right", minWidth: 50 }}>{fmt(Math.round(g.totalDistance))} {economy.distanceLabel()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SpeedView({ segments, economy, isPro, requirePro, onBack }: {
  segments: SpeedSegment[]; economy: ReturnType<typeof makeEconomy>; isPro?: boolean; requirePro?: () => void; onBack: () => void;
}) {
  if (!isPro) return <ProGate title="AVG SPEED" onBack={onBack} requirePro={requirePro} />;

  const [period, setPeriod] = useState("all");
  const { months, years } = useMemo(() => extractPeriods(segments.map((s) => s.date)), [segments]);
  const filtered = useMemo(() => filterByPeriod(segments, (s) => s.date, period), [segments, period]);

  const values = filtered.map((s) => s.speed);
  const avgSpeed = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const totalDist = filtered.reduce((s, seg) => s + seg.distance, 0);
  const c = "var(--cyan)";
  const newest = [...filtered].reverse();

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">AVG SPEED</div>

        <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={segments.length} />

        <div className="statstrip" style={{ marginTop: 12 }}>
          <Tile k="AVG SPEED" v={fmt(Math.round(avgSpeed * 10) / 10)} u={`km/h`} />
          <Tile k="TOTAL DIST" v={fmt(Math.round(totalDist))} u={economy.distanceLabel()} />
        </div>

        {values.length >= 2 && (
          <div className="panel" style={{ marginTop: 16 }}><SplineChart values={values} color={c} height={80} /></div>
        )}

        <div className="divider" style={{ marginTop: 18 }}><span className="label">SEGMENTS · NEWEST FIRST</span><span className="line" /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {newest.map((seg, i) => (
            <div key={i} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
              <span className="mono muted" style={{ fontSize: 11 }}>{longDate(seg.date)}</span>
              <span className="mono" style={{ fontSize: 13 }}>{fmt(seg.speed)} <span className="muted" style={{ fontSize: 9, marginLeft: 4 }}>km/h</span></span>
              <span className="mono muted" style={{ fontSize: 10 }}>{fmt(Math.round(seg.distance))} {economy.distanceLabel()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProGate({ title, onBack, requirePro }: { title: string; onBack: () => void; requirePro?: () => void }) {
  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">PRO FEATURE</div>
        <div className="eyebrow amber">{title}</div>
        <p className="mono" style={{ color: "var(--creamDim)", fontSize: 13, lineHeight: 1.6, marginTop: 14 }}>
          Full {title.toLowerCase()} data, detailed tables, and all trends are part of CarSpend Pro.
        </p>
        <button className="btn primary" style={{ marginTop: 18 }} onClick={requirePro}>UNLOCK PRO</button>
      </div>
    </div>
  );
}

function FilteredEntries({ logs, economy, currency, filter, onBack }: {
  logs: FuelLog[]; economy: ReturnType<typeof makeEconomy>; currency: string; filter: { type: string; value: string }; onBack: () => void;
}) {
  const baseFiltered = useMemo(() => logs.filter((l) => {
    if (filter.type === "station" || filter.type === "consumption") return (l.stationName ?? "").toLowerCase() === filter.value.toLowerCase();
    if (filter.type === "grade") return (l.fuelGrade ?? "").toLowerCase() === filter.value.toLowerCase();
    return true;
  }), [logs, filter]);

  const [period, setPeriod] = useState("all");
  const { months, years } = useMemo(() => extractPeriods(baseFiltered.map((l) => l.date)), [baseFiltered]);
  const filtered = useMemo(() => filterByPeriod(baseFiltered, (l) => l.date, period).sort((a, b) => (b.date ?? 0) - (a.date ?? 0)), [baseFiltered, period]);

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">ENTRIES</div>
        <div className="eyebrow amber">{filter.value}</div>

        <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={baseFiltered.length} />

        <div className="mono muted" style={{ fontSize: 9, marginTop: 10 }}>{filtered.length} {filtered.length === 1 ? "entry" : "entries"}</div>

        {filtered.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No entries found.</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((l) => (
            <LogRow key={l.id} log={l} economy={economy} currency={currency} allLogs={logs} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({ k, v, u }: { k: string; v: string; u?: string }) {
  return (
    <div className="stattile">
      <div className="k">{k}</div>
      <SplitFlapNumber value={v} size={18} weight={500} />
      {u ? <div className="u">{u}</div> : <div style={{ height: 12 }} />}
    </div>
  );
}
